import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import Seo from "@/components/feature/Seo";
import StoryCard from "@/components/feature/StoryCard";
import { getStory, relatedStories, columnBySlug, columnName, formatCount } from "@/lib/content";
import type { ColumnItem } from "@/mocks/site";

export default function StoryPage() {
  const { slug = "" } = useParams();
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const story = getStory(slug);
  const [liked, setLiked] = useState(false);
  const [copied, setCopied] = useState(false);

  if (!story) {
    return (
      <section className="mx-auto max-w-3xl px-4 py-20 text-center md:px-6">
        <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-secondary-100 text-3xl text-secondary-700">
          <i className="ri-error-warning-line" aria-hidden="true" />
        </span>
        <h1 className="mt-5 font-heading text-2xl font-black text-foreground-950">这条瓜文不见了</h1>
        <p className="mt-3 text-sm text-foreground-600">内容可能已被调整，去首页看看最新瓜料吧。</p>
        <Link to="/" className="mt-6 inline-flex items-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 hover:bg-primary-600">
          <i className="ri-home-line" aria-hidden="true" />
          返回首页
        </Link>
      </section>
    );
  }

  const column = columnBySlug(story.column) as ColumnItem | undefined;
  const related = relatedStories(story.column, story.slug, 3);
  const link = origin + `/story/${story.slug}`;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1800);
    } catch {
      /* clipboard unavailable */
    }
  };

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "首页", item: origin + "/" },
        { "@type": "ListItem", position: 2, name: columnName(story.column), item: origin + `/column/${story.column}` },
        { "@type": "ListItem", position: 3, name: story.title },
      ],
    },
    {
      "@context": "https://schema.org",
      "@type": "NewsArticle",
      headline: story.title,
      description: story.summary,
      author: { "@type": "Person", name: story.author },
      datePublished: story.date,
      image: story.cover,
      url: link,
    },
  ];

  return (
    <>
      <Seo
        title={`${story.title}_大学吃瓜`}
        description={`${story.summary} 大学吃瓜${columnName(story.column)}专区，最新校园情感现场，天天更新。`}
        keywords={`${story.tags.join(",")},大学吃瓜,校园情感`}
        type="article"
        jsonLd={jsonLd}
      />
      <article className="mx-auto max-w-3xl px-4 py-10 md:px-6">
        <nav aria-label="面包屑" className="flex flex-wrap items-center gap-1.5 text-sm text-foreground-600">
          <Link to="/" className="flex items-center gap-1 hover:text-primary-700"><i className="ri-home-line text-base" aria-hidden="true" />首页</Link>
          <i className="ri-arrow-right-s-line" aria-hidden="true" />
          <Link to={`/column/${story.column}`} className="hover:text-primary-700">{columnName(story.column)}</Link>
          <i className="ri-arrow-right-s-line" aria-hidden="true" />
          <span aria-current="page" className="line-clamp-1 max-w-[10rem] text-foreground-950">{story.title}</span>
        </nav>

        <header className="mt-6">
          <div className="flex flex-wrap items-center gap-2">
            {column && (
              <Link to={`/column/${column.slug}`} className="rounded-full bg-accent-100 px-3 py-1 text-xs font-medium text-accent-900 hover:bg-accent-200">
                {column.name}
              </Link>
            )}
            {story.tags.map((t) => (
              <span key={t} className="rounded-full bg-secondary-100 px-3 py-1 text-xs text-secondary-900">{t}</span>
            ))}
          </div>
          <h1 className="mt-4 font-heading text-2xl font-black leading-snug text-foreground-950 md:text-4xl">{story.title}</h1>
          <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 border-b border-background-300/60 pb-5 text-sm text-foreground-600">
            <span className="flex items-center gap-1.5">
              <span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary-100 text-[11px] font-bold text-primary-700">{story.author.slice(0, 2)}</span>
              {story.author}
            </span>
            <span className="flex items-center gap-1.5"><i className="ri-calendar-line" aria-hidden="true" />{story.date}</span>
            <span className="flex items-center gap-1.5"><i className="ri-eye-line" aria-hidden="true" />{formatCount(story.views)} 围观</span>
            <span className="flex items-center gap-1.5"><i className="ri-chat-3-line" aria-hidden="true" />{story.comments} 热评</span>
          </div>
        </header>

        <figure className="mt-6 overflow-hidden rounded-xl">
          <img src={story.cover} alt={story.title} className="aspect-[16/9] w-full object-cover object-top" />
        </figure>

        <div className="mt-7 space-y-5 leading-8 text-[15px] text-foreground-800 md:text-base">
          {story.paragraphs.map((p, i) => (
            <p key={i}>{p}</p>
          ))}
        </div>

        <div className="mt-8 flex flex-col gap-4 border-t border-background-300/60 pt-6 sm:flex-row sm:items-center sm:justify-between">
          <button
            type="button"
            onClick={() => setLiked((v) => !v)}
            aria-pressed={liked}
            className={`inline-flex w-fit items-center gap-2 whitespace-nowrap rounded-full border px-5 py-2.5 text-sm font-medium transition-colors ${
              liked ? "border-primary-400 bg-primary-100 text-primary-800" : "border-background-300 text-foreground-700 hover:border-primary-300 hover:text-primary-700"
            }`}
          >
            <i className={liked ? "ri-heart-3-fill text-primary-500" : "ri-heart-3-line"} aria-hidden="true" />
            {liked ? `已点赞 ${formatCount(story.likes + 1)}` : `点赞 ${formatCount(story.likes)}`}
          </button>
          <div className="flex flex-wrap items-center gap-2">
            <button type="button" onClick={copyLink} className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border border-background-300 px-4 py-2 text-sm text-foreground-700 hover:border-primary-300 hover:text-primary-700">
              <i className={copied ? "ri-check-line text-accent-600" : "ri-link"} aria-hidden="true" /> {copied ? "已复制" : "复制链接"}
            </button>
            <Link to={`/column/${story.column}`} className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-secondary-500 px-4 py-2 text-sm text-background-50 hover:bg-secondary-600">
              更多{columnName(story.column)}
              <i className="ri-arrow-right-line" aria-hidden="true" />
            </Link>
          </div>
        </div>
      </article>

      <section className="bg-background-100/70">
        <div className="mx-auto max-w-7xl px-4 py-12 md:px-6">
          <h2 className="mb-6 flex items-center gap-2 font-heading text-xl font-black text-foreground-950 md:text-2xl">
            <i className="ri-restart-line text-primary-500" aria-hidden="true" />
            相关瓜文 · 猜你还想看
          </h2>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:gap-5 lg:grid-cols-3">
            {related.map((s) => (
              <StoryCard key={s.slug} story={s} />
            ))}
          </div>
        </div>
      </section>
    </>
  );
}